// External libraries
import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// FontAwesome icons
import { faCode, faPrintMagnifyingGlass } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Kendo React components
import {
  Button,
  SplitButton,
  SplitButtonItem,
  Toolbar,
  ToolbarSeparator,
} from "@progress/kendo-react-buttons";
import { Window } from "@progress/kendo-react-dialogs";
import { Grid } from "@progress/kendo-react-grid";
import { Loader } from "@progress/kendo-react-indicators";
import { Label } from "@progress/kendo-react-labels";
import { Stepper } from "@progress/kendo-react-layout";

// Internal components
import CopyLinkButton from "../../Buttons/CopyLinkButton";
import { ColorChangingIcon } from "../../Common/UI/ColorChangingIcon";
import { Else, If } from "../../Common/UI/IfComponent";
import { CodeEditor } from "../../Common/UI/JsonCodeEditor";
import { Section } from "../../Common/UI/SectionComponent";
import { AdaptiveField } from "../../Fields/FieldTypes/FieldFactory";
import { EntryPrintPreviewWindow } from "./EntryPrintPreviewWindow";

// Utilities
import { handleAutomations } from "../../Automation/AutomationUtils";
import { deepEqual, handleSignalRTrigger } from "../../Utilities/CommonUtils";
import {
  copyToClipboard,
  detectUnsavedChanges,
  findChangedFields,
  getEntryValues,
  getNextEnabledStep,
  getPreviousEnabledStep,
  getRevisionCount,
  initializeSectionsEditEntry,
  initializeSectionsNewEntry,
  isApprovalModeEntry,
  prepareFieldsForSubmission,
  rowRender,
  validateForm,
  validateStageStep,
} from "../../Utilities/EntryUtils";

// Signals
import {
  advancedOptionsEnabled,
  allTemplates,
  getEntriesByTemplateId,
  notification,
  userData,
  userEntries,
  userTemplates,
} from "../../Common/Store/CommonSignals";
import { selectedHamburgerIndex } from "../../Navigation/HamburgerMenu/HamburgerMenuSignals";
import { showTemplatePreviewWindow } from "../../Template/NewTemplateSignals";
import {
  clearEntryFields,
  disabledSections,
  entryEditMode,
  entryId,
  entryValues,
  filePreviewUrl,
  formErrors,
  galleryValues,
  isResyncingGallery,
  isSaving,
  readOnlyEntry,
  resyncGalleryDifferences,
  resyncGalleryName,
  saveButtonText,
  selectedStep,
  selectedTab,
  showAtlasMenu,
  showFilePreview,
  showGalleryResyncWindow,
  showPrintPreviewWindow,
  steps,
  template,
  templateId,
  templateStageSteps,
} from "./EditEntrySignals";

// Constants
const EDIT_MODES = {
  VISUAL: "Visual",
  CODE: "Code",
};

const SAVE_MODES = {
  SAVE: "Save",
  SAVE_CONTINUE: "Save & Continue",
};

const EntryPage = () => {
  const navigate = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  entryId.value = params.get("entryId") || null;
  selectedHamburgerIndex.value = 2;

  const [initialEntryValues, setInitialEntryValues] = useState({});
  const [unsavedChangesAlert, setUnsavedChangesAlert] = useState(false);
  const [hasCheckedApprovalMode, setHasCheckedApprovalMode] = useState(false);

  //#region Initialization Effects

  // Initialize initial entry values for unsaved changes detection
  useEffect(() => {
    if (Object.keys(initialEntryValues).length === 0) {
      setInitialEntryValues({ ...entryValues.value });
    }
  }, [entryValues.value]);

  //#endregion

  //#region Unsaved Changes Tracking

  // Track unsaved changes for alert display
  useEffect(() => {
    if (template.value?.properties?.UnsavedChangesAlert) {
      const hasUnsavedChanges = detectUnsavedChanges(initialEntryValues, deepEqual);
      if (hasUnsavedChanges !== unsavedChangesAlert) {
        setUnsavedChangesAlert(hasUnsavedChanges);
      }
    }
  }, [entryValues.value, initialEntryValues, unsavedChangesAlert]);

  //#endregion

  //#region Data Fetching and Approval Mode

  // Fetch entries and check approval mode
  useEffect(() => {
    const fetchData = async (templateId) => {
      await getEntriesByTemplateId(templateId, "all");
    };

    const fetchEntry = async (templateId, entryId) => {
      if (!templateId || !entryId) return;
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/api/Entry/${entryId}`
      );

      if (response.data) {
        const newEntries = { ...userEntries.value };
        if (!newEntries[templateId]) {
          newEntries[templateId] = [];
        }
        newEntries[templateId].unshift(response.data);
        userEntries.value = newEntries;
      }
    };

    if (!userEntries.value?.[templateId.value]) {
      fetchData(params.get("templateId"));
    }

    if (
      !userEntries.value?.[params.get("templateId")]?.find(
        (entry) => entry.id === params.get("entryId")
      )
    ) {
      fetchEntry(params.get("templateId"), params.get("entryId"));
    }

    if (templateId.value && userTemplates.value.length > 0) {
      const selectedTemplate = userTemplates.value.find(
        (template) => template.id === templateId.value
      );
      if (selectedTemplate) {
        readOnlyEntry.value = isApprovalModeEntry(selectedTemplate, templateId.value);
        setHasCheckedApprovalMode(true);
      }
    }
  }, [templateId.value, userTemplates.value, entryId.value]);

  //#endregion

  //#region Template and Entry Initialization

  if (!templateId.value) {
    const entryIdParam = params.get("entryId");
    const templateIdParam = params.get("templateId");

    if (entryIdParam) {
      const entry = userEntries.value?.[templateIdParam]?.find(
        (entry) => entry.id === entryIdParam
      );
      if (!userEntries.value?.[templateIdParam] || allTemplates.value?.length === 0 || !entry) {
        return <div>Loading...</div>;
      } else {
        const entryItem = userEntries.value[templateIdParam]?.find(
          (entry) => entry.id === entryIdParam
        );

        entryValues.value = getEntryValues(entryItem);
        template.value = allTemplates.value.find((x) => x.id === entryItem?.templateId);

        const updatedSections = initializeSectionsEditEntry(entryItem, getRevisionCount);

        templateId.value = templateIdParam;
        template.value = {
          ...entryItem,
          id: entryItem.id,
          properties: template.value.properties,
          templateName: template.value.templateName,
          sections: updatedSections,
        };
        entryId.value = entryIdParam;
        selectedTab.value = 0;
        selectedStep.value = 0;
        steps.value = templateStageSteps.value?.map((step) => ({ label: step.text })) ?? [];
      }
    } else if (allTemplates.value?.length === 0) {
      return <div>Loading...</div>;
    } else {
      galleryValues.value = [];
      templateId.value = templateIdParam;
      template.value = allTemplates.value.find((x) => x.id === templateId.value);

      const updatedSections = initializeSectionsNewEntry(template.value.sections, getRevisionCount);

      template.value = { ...template.value, sections: updatedSections };
      entryId.value = entryIdParam ?? null;
      entryValues.value = getEntryValues(template.value);
      formErrors.value = {};
      disabledSections.value = {};
      selectedTab.value = 0;
      selectedStep.value = 0;
      saveButtonText.value = SAVE_MODES.SAVE;
      steps.value = templateStageSteps.value?.map((step) => ({ label: step.text })) ?? [];
    }
  }

  //#endregion

  //#region Save Handling
  const saveHandler = async (templateData, formData) => {
    let response;
    if (entryId.value) {
      response = await axios.put(
        `${process.env.REACT_APP_API_ENDPOINT}/api/Entry/${template.value.id}?userId=${
          userData.value?.userEmail
        }&templateSync=${templateData.properties?.["TemplateSync"] ?? false}`,
        formData
      );

      await handleSignalRTrigger("UPDATE", "Entry", formData);
      handleAutomations({ trigger: "Entry Updated", entryData: response.data });
    } else {
      response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/api/Entry?userId=${
          userData.value?.userEmail
        }&templateSync=${templateData.properties["TemplateSync"] ?? false}`,
        formData
      );

      await handleSignalRTrigger("CREATE", "Entry", response.data);
      handleAutomations({ trigger: "Entry Created", entryData: response.data });
    }
    return response;
  };

  // Main submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    await validateForm();

    const isFormInvalid = Object.values(formErrors.value).some(
      (sectionErrors) => sectionErrors.length > 0
    );

    if (isFormInvalid) return;

    try {
      isSaving.value = true;

      const currentEntryValues = entryValues.value;
      const updatedFields = findChangedFields(initialEntryValues, currentEntryValues);
      const templateData = { ...template.value };

      // Prepare all fields for submission
      prepareFieldsForSubmission(templateData, updatedFields);

      // Build formData
      const formData = {
        id: entryId.value || "",
        actionId: templateData.actionId || null,
        entryAuthor: templateData.entryAuthor
          ? templateData.entryAuthor
          : userData.value?.userEmail,
        templateId: templateId.value,
        sections: templateData.sections,
        _ts: templateData._ts,
        createdDate: templateData.createdDate,
      };

      if (formData.id) {
        const newEntries = { ...userEntries.value };
        const entryIndex = newEntries[templateId.value].findIndex(
          (entry) => entry.id === formData.id
        );
        newEntries[templateId.value][entryIndex] = formData;
        userEntries.value = newEntries;
      } else {
        const newEntries = { ...userEntries.value };
        if (!newEntries[templateId.value]) {
          newEntries[templateId.value] = [];
        }
        newEntries[templateId.value].unshift(formData);
        userEntries.value = newEntries;
      }

      // Submit to API
      console.log("Submitting entry...", formData);
      let response = await saveHandler(templateData, formData);
      console.log("Entry submitted successfully:", response);

      // Handle success
      const templateEntries = [...userEntries.value[templateId.value]];

      if (!formData.id) {
        templateEntries.shift();
        templateEntries.unshift(response.data);
        userEntries.value = { ...userEntries.value, [templateId.value]: templateEntries };
      }

      notification.value = {
        show: true,
        type: "success",
        message: `Entry successfully submitted for template ${template.value.templateName}`,
      };

      if (saveButtonText.value === SAVE_MODES.SAVE) {
        if (showAtlasMenu.value) {
          navigate(`/view-entry?templateId=${templateData.id}`);
        } else {
          clearEntryFields.value = true;

          const updatedSections = initializeSectionsNewEntry(
            template.value.sections,
            getRevisionCount
          );
          template.value = { ...template.value, sections: updatedSections };
        }
      } else {
        setInitialEntryValues({ ...entryValues.value });
      }
    } catch (error) {
      console.log("Error response:", error, error.response);

      notification.value = {
        show: true,
        type: "error",
        message: `Failed to submit entry for template ${template.value.templateName}`,
        error: error,
      };

      isSaving.value = false;
      clearEntryFields.value = false;
    } finally {
      isSaving.value = false;
      clearEntryFields.value = false;
      if (saveButtonText.value === SAVE_MODES.SAVE) {
        selectedStep.value = 0;
        disabledSections.value = {};
      }
    }
  };

  const handleFieldChange = ({ e, sectionIndex, fieldIndex }) => {
    const newTemplate = JSON.tryParse(JSON.stringify(template.value));
    const fieldValue = e?.value?.toString() ?? "";
    newTemplate.sections[sectionIndex].fields[fieldIndex].value = fieldValue;
    const field = newTemplate.sections[sectionIndex].fields[fieldIndex];

    if (!e.unmounted) {
      entryValues.value = {
        ...entryValues.value,
        [field.fieldName]: fieldValue,
      };
    }

    template.value = newTemplate;
  };

  //#endregion

  //#region Render Guards

  if (!hasCheckedApprovalMode) {
    return <div>Loading...</div>;
  }

  //#endregion

  //#region Main Render

  return (
    <div className="k-d-flex k-flex-col k-h-full k-overflow-auto">
      {!showTemplatePreviewWindow.value && (
        <Toolbar
          size={null}
          className="k-w-full"
        >
          <If condition={!isSaving.value}>
            <SplitButton
              style={{ height: "80%", marginLeft: "5px", marginRight: "5px" }}
              name="btnTemplateSubmit"
              text={saveButtonText.value}
              onItemClick={(e) => {
                saveButtonText.value = e.item.text;
                handleSubmit(e.nativeEvent);
              }}
              onButtonClick={(e) => handleSubmit(e.nativeEvent)}
              themeColor={"success"}
            >
              <SplitButtonItem text={SAVE_MODES.SAVE} />
              <SplitButtonItem
                text={SAVE_MODES.SAVE_CONTINUE}
                disabled={entryId.value === null}
              />
            </SplitButton>
            <Else>
              <Loader
                type="converging-spinner"
                themeColor="primary"
                margin="7px 10px"
              />
            </Else>
          </If>
          {readOnlyEntry.value && (
            <Label
              className={"k-pl-1 k-pr-2"}
              style={{ color: "#02ADED", fontWeight: "bold" }}
            >
              Approval Mode
            </Label>
          )}
          <ToolbarSeparator />
          {Object.values(formErrors.value).some((section) => section?.length > 0) && (
            <Label
              className={"k-pl-4 k-pr-4"}
              editorValid={false}
            >
              There are missing required fields
            </Label>
          )}
          {Object.values(formErrors.value).some((section) => section?.length > 0) &&
            template?.value?.properties?.UnsavedChangesAlert &&
            unsavedChangesAlert && <ToolbarSeparator />}
          {template?.value?.properties?.UnsavedChangesAlert && unsavedChangesAlert && (
            <Label
              className={"k-pl-4 k-pr-4"}
              editorValid={false}
            >
              There are unsaved changes in the entry
            </Label>
          )}

          <div className="aling-right">
            <If condition={advancedOptionsEnabled.value}>
              <div>
                <Button
                  className="!k-fs-xl !k-rounded-0"
                  style={{ padding: "5px 5px 5px 5px" }}
                  fillMode={"flat"}
                  onClick={() => {
                    if (entryEditMode.value === EDIT_MODES.VISUAL) {
                      entryEditMode.value = EDIT_MODES.CODE;
                    } else {
                      entryEditMode.value = EDIT_MODES.VISUAL;
                    }
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCode}
                    className="k-mr-1"
                    flip="vertical"
                  />
                </Button>
              </div>
            </If>

            <ColorChangingIcon
              icon={faPrintMagnifyingGlass}
              name={"btnPrintWindow_" + template.value?.templateName}
              initialPrimaryColor="#0078D4"
              padding="6px 10px 0 0"
              onClick={() => (showPrintPreviewWindow.value = true)}
            />

            <CopyLinkButton
              name={"btnCopyLink_" + template.value?.templateName}
              onClick={copyToClipboard}
            />
          </div>
        </Toolbar>
      )}
      <form
        onSubmit={(e) => handleSubmit(e)}
        className="!k-rounded-bl-xl !k-rounded-br-xl k-bg-white k-p-2 !k-h-full k-overflow-auto k-border-toolbar k-border-solid k-border-1"
      >
        {entryEditMode.value === EDIT_MODES.VISUAL && (
          <div>
            <h1>{template.value?.templateName}</h1>
            <legend>
              {template.value?.actionId && <div>{"Action Id: " + template.value?.actionId}</div>}
              {template?.value?.sections?.map((section) => {
                return section?.fields?.map((field) => {
                  if (field?.properties?.RevisionCount) {
                    return (
                      <div key={field?.fieldName + "_revisionCount"}>
                        {field?.fieldName + " Revision Count: " + field?.revisionCount}
                      </div>
                    );
                  } else {
                    return null;
                  }
                });
              })}
              {template?.value?.sections?.map((section) => {
                return section?.fields?.map((field) => {
                  if (field.fieldType === "Index") {
                    return (
                      <div key={field?.fieldName}>
                        <AdaptiveField
                          field={field}
                          value={field.value}
                          fieldType={field.fieldType}
                          handleFieldChange={handleFieldChange}
                          templateId={params.get("templateId")}
                        />
                      </div>
                    );
                  } else {
                    return null;
                  }
                });
              })}
              <If condition={templateStageSteps.value?.length > 0}>
                <Stepper
                  value={selectedStep.value}
                  items={steps.value?.map((step, index) => ({
                    ...step,
                    style: {
                      ...step.style,
                      paddingBottom: "10px",
                      minWidth: "150px",
                      position: "relative",
                      marginRight: index !== steps.value.length - 1 ? "40px" : "0px",
                    },
                    label: (
                      <div className="k-step-custom">
                        {/* Horizontal progress bar - blue */}
                        {index > 0 && index <= selectedStep.value && (
                          <div className="k-stepper-blue-progress-bar-custom" />
                        )}
                        {/* Horizontal progress bar - gray */}
                        {index > 0 && index > selectedStep.value && (
                          <div className="k-stepper-gray-progress-bar-custom" />
                        )}
                        <span
                          style={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            textAlign: "center",
                            padding: "0 10px",
                          }}
                        >
                          {step.label}
                        </span>
                      </div>
                    ),
                  }))}
                  mode={"labels"}
                  onChange={async (e) => {
                    await validateStageStep();
                    selectedTab.value = 0;
                    selectedStep.value = e.value;
                  }}
                  style={{
                    marginBottom: "20px",
                    marginTop: "15px",
                    fontSize: "18px",
                  }}
                />
              </If>
            </legend>

            <div className="separator" />

            {/* Sections render */}
            <Section handleSubmit={handleSubmit}></Section>

            {/* Step buttons render */}
            {templateStageSteps.value?.length > 0 && (
              <div
                className="k-d-flex"
                style={{ marginLeft: "85%", marginTop: "5%" }}
              >
                {selectedStep.value > 0 &&
                  !steps.value.slice(0, selectedStep.value).every((step) => step.disabled) && (
                    <Button
                      style={{ marginRight: "10px", fontSize: "16px" }}
                      themeColor={"light"}
                      type="button"
                      onClick={() => {
                        validateStageStep();
                        selectedTab.value = 0;
                        selectedStep.value = getPreviousEnabledStep(
                          selectedStep.value,
                          steps.value
                        );
                      }}
                    >
                      Previous
                    </Button>
                  )}
                {selectedStep.value < steps.value?.length - 1 &&
                  !steps.value.slice(selectedStep.value + 1).every((step) => step.disabled) && (
                    <Button
                      style={{ fontSize: "16px" }}
                      themeColor={"primary"}
                      type="button"
                      onClick={() => {
                        validateStageStep();
                        selectedTab.value = 0;
                        selectedStep.value = getNextEnabledStep(selectedStep.value, steps.value);
                      }}
                    >
                      Next
                    </Button>
                  )}
              </div>
            )}
          </div>
        )}

        {entryEditMode.value === EDIT_MODES.CODE && (
          <CodeEditor
            initialCode={template.value}
            language={"json"}
            type={"Entry"}
          />
        )}
      </form>
      {showGalleryResyncWindow.value && (
        <Window
          title={`Resync ${resyncGalleryName.value} Data Connection`}
          className="k-overflow-y-hidden"
          onClose={() => (showGalleryResyncWindow.value = false)}
          initialHeight={600}
          initialWidth={1500}
          draggable={false}
          modal="false"
        >
          <div className="k-d-flex k-flex-col k-h-full k-overflow-auto">
            <div className="k-d-flex k-mb-3">
              <Button
                className="k-w-20 k-ml-2"
                themeColor={"success"}
                disabled={resyncGalleryDifferences.value.additions.length === 0}
                onClick={() => {
                  isResyncingGallery.value = true;
                }}
              >
                Accept
              </Button>
              <Button
                className="k-w-20 k-ml-3"
                themeColor={"error"}
                onClick={() => {
                  showGalleryResyncWindow.value = false;
                  resyncGalleryDifferences.value = {};
                }}
              >
                Reject
              </Button>
            </div>
            <div className="k-p-2 k-overflow-y-auto">
              <p>
                The following differences were found between the data connection and the gallery
                data. Please review the differences and select which data to keep.
              </p>
              <Grid
                data={[
                  ...(entryValues.value[resyncGalleryName.value] || []),
                  ...resyncGalleryDifferences.value.additions,
                ]}
                rows={{
                  data: rowRender,
                }}
              />
            </div>
          </div>
        </Window>
      )}
      <If condition={showFilePreview.value}>
        <Window
          title={"File Preview"}
          onClose={() => (showFilePreview.value = false)}
          initialHeight={800}
          initialWidth={1200}
        >
          <img
            src={filePreviewUrl.value}
            alt="Preview of uploaded file to blob storage"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        </Window>
      </If>
      <EntryPrintPreviewWindow />
    </div>
  );

  //#endregion
};

export default EntryPage;
